import * as React from 'react'
import { Avatar, Box, Card, CardContent, Chip, Collapse, FormControlLabel, IconButton, Link, MenuItem, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import useAutoTranslate from '../hooks/useAutoTranslate'

const REFRESH_MS = 15 * 60 * 1000
const STORAGE_KEY = 'ggeEventCalendar:v1'
const PREFERENCES_KEY = 'ggeEventCalendarView:v1'
const SERVER_EVENT_CARDS = { 71: 'war-of-the-realms', 72: 'nomad-invasion', 80: 'samurai-invasion',
    85: 'berimond', 103: 'bloodcrow-invasion', 106: 'outer-realms', 113: 'beyond-the-horizon', 133: 'rift-raid' }
export const getServerTimersForCard = (event, timers) => timers.filter(timer => SERVER_EVENT_CARDS[timer.id] === event.id)
export const formatCalendarDate = (value, timeZone) => {
    if (value.length === 10) return `${value.slice(8, 10)}/${value.slice(5, 7)}`
    const parts = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', ...(timeZone ? { timeZone } : {}) }).formatToParts(new Date(value))
    const part = type => parts.find(entry => entry.type === type)?.value
    return `${part('day')}/${part('month')}  ${part('hour')}:${part('minute')}`
}

// Pair only an unambiguous current occurrence; never attach a live timer to a future run.
export const getServerEndForInterval = (event, interval, timers, now) => {
    if (interval.start.length !== 10 || interval.end.length !== 10) return null
    const current = event.intervals.filter(entry => { const [start, end] = getCalendarIntervalBounds(entry); return start <= now && now < end })
    if (current.length !== 1 || current[0] !== interval) return null
    const matching = getServerTimersForCard(event, timers)
    return matching.length === 1 ? matching[0] : null
}
const readPreferences = () => {
    try { return JSON.parse(localStorage.getItem(PREFERENCES_KEY)) || {} }
    catch { return {} }
}
const validCalendar = data => data?.version === 1 && Array.isArray(data.events) && data.events.every(event =>
    typeof event.id === 'string' && typeof event.name === 'string' && Array.isArray(event.intervals) &&
    event.intervals.every(interval => Number.isFinite(Date.parse(interval.start)) &&
        (interval.start.length === 10 && interval.end.length === 10 ? Date.parse(interval.end) >= Date.parse(interval.start) : Date.parse(interval.end) > Date.parse(interval.start))))

export const getCalendarIntervalBounds = interval => {
    if (interval.start.length !== 10) return [Date.parse(interval.start), Date.parse(interval.end)]
    const start = new Date(interval.start + 'T00:00:00')
    const end = new Date(interval.end + 'T00:00:00')
    end.setDate(end.getDate() + 1)
    return [start.getTime(), end.getTime()]
}

export const isCalendarEventActive = (event, now) => event.intervals.some(interval => {
    const [start, end] = getCalendarIntervalBounds(interval)
    return start <= now && now < end
})

export const getCalendarCountdown = (interval, now) => {
    // Published dates alone cannot provide a truthful countdown.
    if (interval.start.length === 10 || interval.end.length === 10) return null
    const [start, end] = getCalendarIntervalBounds(interval)
    if (!Number.isFinite(start) || !Number.isFinite(end) || now >= end) return null
    return { kind: now < start ? 'start' : 'end', remainingSeconds: Math.ceil(((now < start ? start : end) - now) / 1000) }
}

const formatCountdown = seconds => {
    const days = Math.floor(seconds / 86400)
    const clock = [Math.floor(seconds / 3600) % 24, Math.floor(seconds / 60) % 60, seconds % 60]
        .map(value => String(value).padStart(2, '0')).join(':')
    return days ? `${days}d ${clock}` : clock
}

export default function EventCalendar({ appLanguage = 'en', users = [], usersStatus = {} }) {
    const t = useAutoTranslate(appLanguage, ['GGE Event Calendar', 'Informational only', 'Active', 'Upcoming',
        'Ended', 'No event schedule has been published yet.', 'Source unavailable. Showing the last valid calendar.',
        'Times are shown in your local timezone.', 'Loading calendar…', 'Official source', 'Active (calendar)',
        'Dates only: the source does not publish exact hours. Active status is indicative.',
        'Minimize calendar', 'Maximize calendar', 'Show remaining time', 'Starts in', 'Ends in',
        'Live game timers', 'Ends at', 'Game server timer; start time not supplied.', 'Last received',
        'Active (server)', 'Monthly dates only; confirmed server end times appear on active cards.',
        'No confirmed server timers received yet.', 'Calendar server', 'Unknown start time',
        'Samurai Invasion', 'Nomad Invasion', 'Bloodcrow Invasion', 'War of the Realms', 'Berimond',
        'Bladecoast', 'Rift Raid', 'Grand Tournament', 'Beyond the Horizon', 'Outer Realms',
        'Imperial Patronage', 'Grand Nobility Contest'])
    const [calendar, setCalendar] = React.useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(STORAGE_KEY))
            return validCalendar(saved?.calendar) ? saved.calendar : null
        } catch { return null }
    })
    const [stale, setStale] = React.useState(false)
    const [now, setNow] = React.useState(Date.now)
    const [collapsed, setCollapsed] = React.useState(() => readPreferences().collapsed === true)
    const [showCountdown, setShowCountdown] = React.useState(() => readPreferences().showCountdown === true)
    const [pageVisible, setPageVisible] = React.useState(() => !document.hidden)
    const [selectedBotId, setSelectedBotId] = React.useState(() => String(readPreferences().selectedBotId || ''))
    React.useEffect(() => {
        try { localStorage.setItem(PREFERENCES_KEY, JSON.stringify({ collapsed, showCountdown, selectedBotId })) } catch { /* Optional preferences. */ }
    }, [collapsed, showCountdown, selectedBotId])

    React.useEffect(() => {
        let disposed = false, timer, controller, etag = '', nextCheck = 0
        try {
            const saved = JSON.parse(localStorage.getItem(STORAGE_KEY))
            if (validCalendar(saved?.calendar)) etag = saved.etag || ''
        } catch { /* Storage can be unavailable. */ }
        const schedule = () => {
            clearTimeout(timer)
            if (!disposed && !document.hidden) timer = setTimeout(refresh, Math.max(1000, nextCheck - Date.now()))
        }
        const refresh = async () => {
            if (disposed || document.hidden) return
            if (controller) return
            controller = new AbortController()
            const timeout = setTimeout(() => controller?.abort(), 12000)
            try {
                const response = await fetch('/event-calendar.json', {
                    headers: etag ? { 'If-None-Match': etag } : {}, signal: controller.signal, cache: 'no-cache'
                })
                if (response.status !== 304) {
                    if (!response.ok) throw new Error('Calendar source unavailable')
                    const data = await response.json()
                    if (!validCalendar(data)) throw new Error('Invalid calendar')
                    if (disposed) return
                    etag = response.headers.get('etag') || ''
                    setCalendar(data)
                    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ calendar: data, etag })) } catch { /* Optional cache. */ }
                }
                if (!disposed) setStale(response.headers.get('x-calendar-stale') === '1')
                const retrySeconds = Number(response.headers.get('retry-after'))
                nextCheck = Date.now() + (retrySeconds > 0 ? Math.max(REFRESH_MS, retrySeconds * 1000) : REFRESH_MS)
            } catch {
                if (!disposed) setStale(true)
                nextCheck = Date.now() + REFRESH_MS
            } finally {
                clearTimeout(timeout)
                controller = null
                schedule()
            }
        }
        const onVisibility = () => {
            clearTimeout(timer)
            setPageVisible(!document.hidden)
            if (!document.hidden) {
                setNow(Date.now())
                if (Date.now() >= nextCheck) refresh()
                else schedule()
            }
        }
        document.addEventListener('visibilitychange', onVisibility)
        refresh()
        return () => {
            disposed = true
            clearTimeout(timer)
            controller?.abort()
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [])

    const selectedBot = users.find(user => String(user.id) === selectedBotId) || users.find(user => usersStatus?.[user.id]?.liveEventCalendar?.events?.length) || users[0]
    const liveTimers = (selectedBot ? [selectedBot] : []).flatMap(user => {
        const snapshot = usersStatus?.[user.id]?.liveEventCalendar
        if (snapshot?.source !== 'game:sei' || !Array.isArray(snapshot.events)) return []
        return snapshot.events.filter(event => Number.isFinite(Date.parse(event.endsAt)) && Date.parse(event.endsAt) > now)
            .map(event => ({ ...event, botId: user.id, botName: user.name || user.id, receivedAt: snapshot.receivedAt }))
    })
    const liveBoundary = liveTimers.reduce((end, event) => Math.min(end, Date.parse(event.endsAt)), Infinity)
    const unmatchedTimers = liveTimers.filter(timer => !calendar?.events.some(event => SERVER_EVENT_CARDS[timer.id] === event.id))

    // A single timer at the next start/end changes the badge without requesting data.
    React.useEffect(() => {
        if (!pageVisible || collapsed) return undefined
        let boundary = liveBoundary
        for (const event of calendar?.events || []) {
            for (const interval of event.intervals) {
                for (const time of getCalendarIntervalBounds(interval)) {
                    if (time > now && time < boundary) boundary = time
                }
            }
        }
        if (!Number.isFinite(boundary)) return undefined
        const timer = setTimeout(() => setNow(Date.now()), Math.min(2147483647, Math.max(1, boundary - Date.now() + 20)))
        return () => clearTimeout(timer)
    }, [calendar, now, pageVisible, collapsed, liveBoundary])

    const countdownAvailable = liveTimers.length > 0 || calendar?.events.some(event => event.intervals.some(interval => getCalendarCountdown(interval, now)))
    React.useEffect(() => {
        if (!showCountdown || !countdownAvailable || collapsed || !pageVisible) return undefined
        // One clock for all intervals; no network requests and no per-card timers.
        const timer = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(timer)
    }, [showCountdown, countdownAvailable, collapsed, pageVisible])

    const dateOnly = calendar?.events.some(event => event.intervals.some(interval => interval.start.length === 10))
    const formatDate = formatCalendarDate
    return (
        <Box component="section" aria-label={t('GGE Event Calendar')}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('GGE Event Calendar')}</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                    <Chip size="small" label={t('Informational only')} variant="outlined" />
                    <Tooltip title={t(collapsed ? 'Maximize calendar' : 'Minimize calendar')}>
                        <IconButton aria-label={t(collapsed ? 'Maximize calendar' : 'Minimize calendar')} aria-expanded={!collapsed} aria-controls="gge-event-calendar-content" onClick={() => {
                            setNow(Date.now())
                            setCollapsed(value => !value)
                        }}>
                            {collapsed ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowUpRoundedIcon />}
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>
            <Collapse in={!collapsed} unmountOnExit>
            <Box id="gge-event-calendar-content">
            {calendar?.source && <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <Link href={calendar.source.url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 13 }}>{t('Official source')}: {calendar.source.name}</Link>
                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{calendar.source.period}</Typography>
            </Stack>}
            {users.length > 1 && <TextField select size="small" label={t('Calendar server')} value={String(selectedBot?.id || '')} onChange={event => {
                setSelectedBotId(event.target.value)
                setNow(Date.now())
            }} sx={{ mb: 1.5, minWidth: 220, maxWidth: '100%' }}>
                {users.map(user => <MenuItem key={user.id} value={String(user.id)}>{user.name || user.id}</MenuItem>)}
            </TextField>}
            {users.length === 1 && <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary' }}>{t('Calendar server')}{': '}{selectedBot?.name || selectedBot?.id}</Typography>}
            {dateOnly && <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary' }}>{t(liveTimers.length ? 'Monthly dates only; confirmed server end times appear on active cards.' : 'Dates only: the source does not publish exact hours. Active status is indicative.')}</Typography>}
            {!liveTimers.length && Boolean(users.length) && <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary' }}>{t('No confirmed server timers received yet.')}</Typography>}
            {unmatchedTimers.length > 0 && <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 800, mb: 0.5 }}>{t('Live game timers')}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('Game server timer; start time not supplied.')}{' '}{t('Times are shown in your local timezone.')}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1, mt: 1 }}>
                    {unmatchedTimers.map(event => <Card key={`${event.botId}:${event.id}`} variant="outlined">
                        <CardContent>
                            <Typography sx={{ fontWeight: 700 }}>{t(event.name)}</Typography>
                            <Typography sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', whiteSpace: 'pre-wrap' }}>{t('Unknown start time')}{' → '}<time dateTime={event.endsAt}>{formatDate(event.endsAt)}</time></Typography>
                            {showCountdown && <Typography variant="caption" display="block">{t('Ends in')}{': '}{formatCountdown(Math.max(0, Math.ceil((Date.parse(event.endsAt) - now) / 1000)))}</Typography>}
                        </CardContent>
                    </Card>)}
                </Box>
            </Box>}
            {countdownAvailable && <FormControlLabel sx={{ mb: 1 }} control={<Switch checked={showCountdown} onChange={event => {
                setNow(Date.now())
                setShowCountdown(event.target.checked)
            }} inputProps={{ 'aria-label': t('Show remaining time') }} />} label={t('Show remaining time')} />}
            {stale && <Typography role="status" sx={{ color: 'warning.main', mb: 1, fontSize: 13 }}>{t('Source unavailable. Showing the last valid calendar.')}</Typography>}
            {!calendar?.events.length && <Typography sx={{ color: 'text.secondary', py: 2 }}>{t(calendar ? 'No event schedule has been published yet.' : stale ? 'No event schedule has been published yet.' : 'Loading calendar…')}</Typography>}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
                {(calendar?.events || []).map(event => {
                    const serverTimers = getServerTimersForCard(event, liveTimers)
                    const active = serverTimers.length > 0 || isCalendarEventActive(event, now)
                    const upcoming = event.intervals.some(interval => getCalendarIntervalBounds(interval)[0] > now)
                    const dayBased = event.intervals.some(interval => interval.start.length === 10)
                    return (
                        <Card key={event.id} sx={{ border: '1px solid', borderColor: active ? 'rgba(112,193,154,0.5)' : 'rgba(129,146,178,0.18)', background: active ? 'linear-gradient(135deg, rgba(53,98,79,0.24), rgba(24,35,56,0.86))' : 'rgba(24,35,56,0.86)', borderRadius: 2 }}>
                            <CardContent>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                                    <Avatar variant="rounded" src={event.image || undefined} alt="" imgProps={{ loading: 'lazy', referrerPolicy: 'no-referrer' }} sx={{ width: 52, height: 52, bgcolor: 'rgba(143,180,255,0.12)', color: '#8fb4ff' }}><EventRoundedIcon /></Avatar>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>{t(event.name)}</Typography>
                                        <Chip size="small" label={t(serverTimers.length ? 'Active (server)' : active ? dayBased ? 'Active (calendar)' : 'Active' : upcoming ? 'Upcoming' : 'Ended')} color={active ? 'success' : 'default'} variant="outlined" sx={{ mt: 0.5 }} />
                                    </Box>
                                </Stack>
                                <Stack spacing={1}>{event.intervals.map((interval, index) => {
                                    const serverEnd = getServerEndForInterval(event, interval, liveTimers, now)
                                    const end = serverEnd?.endsAt || interval.end
                                    const countdown = showCountdown && (serverEnd ? { kind: 'end', remainingSeconds: Math.ceil((Date.parse(end) - now) / 1000) } : getCalendarCountdown(interval, now))
                                    return <Box key={index}>
                                        <Typography sx={{ color: serverEnd ? 'text.primary' : 'text.secondary', fontSize: 13, fontVariantNumeric: 'tabular-nums', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{interval.label && <Box component="span" sx={{ fontWeight: 700 }}>{interval.label}{': '}</Box>}<time dateTime={interval.start}>{formatDate(interval.start)}</time>{' → '}<time dateTime={end}>{formatDate(end)}</time></Typography>
                                        {countdown && <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{t(countdown.kind === 'start' ? 'Starts in' : 'Ends in')}{': '}{formatCountdown(countdown.remainingSeconds)}</Typography>}
                                    </Box>
                                })}</Stack>
                                {serverTimers.filter(timer => !event.intervals.some(interval => getServerEndForInterval(event, interval, liveTimers, now) === timer)).map(timer => <Typography key={timer.id} sx={{ mt: 1, fontSize: 13, fontVariantNumeric: 'tabular-nums', whiteSpace: 'pre-wrap' }}>{t('Unknown start time')}{' → '}<time dateTime={timer.endsAt}>{formatDate(timer.endsAt)}</time></Typography>)}
                                {event.notes && <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'warning.main' }}>{event.notes}</Typography>}
                            </CardContent>
                        </Card>
                    )
                })}
            </Box>
            {Boolean(calendar?.events.length) && !dateOnly && <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>{t('Times are shown in your local timezone.')}</Typography>}
            </Box>
            </Collapse>
        </Box>
    )
}
